import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Fragment } from 'preact/compat';
import FontFaceObserver from 'fontfaceobserver';
import PropTypes from 'prop-types';
import {
  checkIsEmptyFontFile,
  getCssFontSource,
} from './CustomFontLoader.utils';

const FONT_LOAD_STATE = {
  init: 'init',
  success: 'success',
  error: 'error',
};
const CUSTOM_FONT_NAME = 'ppFont';
const REGULAR_FONT_WEIGHT = 400;
const BOLD_FONT_WEIGHT = 700;
const FONT_FILE_LOAD_TIMEOUT = 1000;

const FontLoader = ({ children, fallbackFont, customFontFiles }) => {
  const isEmptyFontFile = checkIsEmptyFontFile(customFontFiles);
  const [fontState, setFontState] = useState(FONT_LOAD_STATE.init);
  const fontToLoad =
    fontState === FONT_LOAD_STATE.error ? fallbackFont : CUSTOM_FONT_NAME;

  useEffect(() => {
    if (isEmptyFontFile) {
      return;
    }

    const regularFontObserver = new FontFaceObserver(CUSTOM_FONT_NAME, {
      weight: REGULAR_FONT_WEIGHT,
    });

    const boldFontObserver = new FontFaceObserver(CUSTOM_FONT_NAME, {
      weight: BOLD_FONT_WEIGHT,
    });

    Promise.all([
      regularFontObserver.load(null, FONT_FILE_LOAD_TIMEOUT),
      boldFontObserver.load(null, FONT_FILE_LOAD_TIMEOUT),
    ])
      .then(() => {
        setFontState(FONT_LOAD_STATE.success);
      })
      .catch(() => {
        setFontState(FONT_LOAD_STATE.error);
      });
  }, []);

  if (isEmptyFontFile) {
    return <div style={{ fontFamily: fallbackFont }}>{children}</div>;
  }

  return (
    <Fragment>
      <style
        // eslint-disable-next-line
        dangerouslySetInnerHTML={{
          __html: `
            @font-face {
              font-family: ${CUSTOM_FONT_NAME};
              font-weight: ${REGULAR_FONT_WEIGHT};
              src: ${getCssFontSource(customFontFiles.regular)};
            }
            @font-face {
              font-family: ${CUSTOM_FONT_NAME};
              font-weight: ${BOLD_FONT_WEIGHT};
              src: ${getCssFontSource(customFontFiles.bold)};
            }
          `,
        }}
      />
      {fontState === FONT_LOAD_STATE.init ? (
        ''
      ) : (
        <div style={{ fontFamily: fontToLoad }}>{children}</div>
      )}
    </Fragment>
  );
};

FontLoader.propTypes = {
  children: PropTypes.node.isRequired,
  fallbackFont: PropTypes.string,
  customFontFiles: PropTypes.shape({
    regular: PropTypes.arrayOf(PropTypes.string),
    bold: PropTypes.arrayOf(PropTypes.string),
  }),
};

FontLoader.defaultProps = {
  fallbackFont: undefined, // font family incase can not load the font file
  customFontFiles: { regular: [], bold: [] },
};

export default FontLoader;
